import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import BalanceDisplay from '../components/time-off/BalanceDisplay';
import RequestCard from '../components/time-off/RequestCard';
import StatusBadge from '../components/time-off/StatusBadge';
import Modal from '../components/common/Modal';
import { useToast } from '../components/common/Toast';
import './TimeOff.css';

function TimeOff() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceRes, requestsRes] = await Promise.allSettled([
        api.get(`/users/${user.id}/balance`),
        api.get(`/requests?user_id=${user.id}${statusFilter ? `&status=${statusFilter}` : ''}`),
      ]);
      if (balanceRes.status === 'fulfilled') {
        const b = balanceRes.value;
        setBalance(b.not_configured ? null : b);
      } else {
        setBalance(null);
      }
      setRequests(requestsRes.status === 'fulfilled' ? (requestsRes.value.requests || []) : []);
    } catch (err) {
      // Don't loop -- just show empty state
    } finally {
      setLoading(false);
    }
  }, [user.id, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCancelClick = (requestId) => {
    setCancelRequestId(requestId);
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!cancelRequestId) return;
    setCancelling(true);
    try {
      await api.post(`/requests/${cancelRequestId}/cancel`);
      toast.success('Request cancelled');
      setCancelModalOpen(false);
      setCancelRequestId(null);
      loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'denied', label: 'Denied' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const isW2 = user?.employment_type === 'staff_w2' || balance?.no_balance;

  return (
    <div className="time-off">
      <div className="page-header">
        <Calendar size={24} strokeWidth={2} />
        <h1>Time Off</h1>
        <button className="btn-primary" onClick={() => navigate('/time-off/submit')}>
          <Plus size={16} />
          Request Time Off
        </button>
      </div>

      <div className="time-off-layout">
        {/* Balance Card -- hidden for W2 staff (external tracking) */}
        {isW2 ? (
          <div className="time-off-balance-card">
            <h3>Time Off</h3>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>
              Your PTO is tracked in our benefits system. Entries here are for team calendar visibility and auto-approve.
            </p>
            <div className="balance-year-label">
              You've entered <strong>{balance?.days_taken_this_year || 0}</strong> day{(balance?.days_taken_this_year || 0) === 1 ? '' : 's'} this year
            </div>
            {balance?.birthday?.eligible && (
              <div className="birthday-status">
                <span className="birthday-label">Birthday Day Off</span>
                {balance.birthday.used ? (
                  <span className="birthday-used">
                    <CheckCircle size={12} /> Used
                  </span>
                ) : (
                  <span className="birthday-available">Available this month</span>
                )}
              </div>
            )}
          </div>
        ) : (
        <div className="time-off-balance-card">
          <h3>PTO Balance</h3>
          {balance ? (
            <>
              <div className="balance-year-label">{balance.pto_year?.label}</div>
              <BalanceDisplay balance={balance} />
              {balance.birthday?.eligible && (
                <div className="birthday-status">
                  <span className="birthday-label">Birthday Day Off</span>
                  {balance.birthday.used ? (
                    <span className="birthday-used">
                      <CheckCircle size={12} /> Used
                    </span>
                  ) : (
                    <span className="birthday-available">Available</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="balance-loading">
              {loading ? 'Loading...' : 'No allocation set'}
            </div>
          )}
        </div>
        )}

        {/* Requests List */}
        <div className="time-off-requests">
          <div className="requests-header">
            <h3>My Requests</h3>
            <div className="requests-filters">
              {statusOptions.map(opt => (
                <button
                  key={opt.value}
                  className={`filter-pill ${statusFilter === opt.value ? 'filter-pill-active' : ''}`}
                  onClick={() => setStatusFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="requests-loading">Loading requests...</div>
          ) : requests.length === 0 ? (
            <div className="requests-empty">
              <Clock size={32} strokeWidth={1.5} />
              {statusFilter ? (
                <p>No requests with status "{statusFilter}"</p>
              ) : (
                <>
                  <p>No time off requests yet</p>
                  <p className="requests-empty-hint">Submit your first request to get started.</p>
                </>
              )}
              <button className="btn-secondary" onClick={() => navigate('/time-off/submit')}>
                Submit a Request
              </button>
            </div>
          ) : (
            <div className="requests-list">
              {requests.map(request => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onCancel={handleCancelClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        title="Cancel Request"
        size="small"
      >
        <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-4)' }}>
          Are you sure you want to cancel this time off request?
          {requests.find(r => r.id === cancelRequestId)?.status === 'approved' && (
            <span style={{ display: 'block', marginTop: '8px', color: 'var(--status-denied)', fontSize: 'var(--font-size-sm)' }}>
              This request was already approved. Cancelling will restore your balance.
            </span>
          )}
        </p>
        <div className="modal-actions">
          <button
            className="btn-secondary"
            onClick={() => setCancelModalOpen(false)}
            disabled={cancelling}
          >
            Keep Request
          </button>
          <button
            className="btn-danger"
            onClick={handleCancelConfirm}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Request'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default TimeOff;
